/**
 * @fileoverview Region detection utility for automatic user region assignment
 *
 * Detects user region (continent) using:
 * 1. Primary: Timezone-based detection (most accurate)
 * 2. Fallback: Language/country code detection
 */

export type Region = 'NA' | 'SA' | 'EU' | 'AF' | 'AS' | 'OC' | 'UNKNOWN';

export interface RegionInfo {
  region: Region;
  timezone?: string;
  language?: string;
  detectionMethod: 'timezone' | 'language' | 'unknown';
}

/**
 * Timezone to continent mapping
 * Maps IANA timezone identifiers to regions
 */
const TIMEZONE_TO_REGION: Record<string, Region> = {
  // North America
  'America/New_York': 'NA',
  'America/Chicago': 'NA',
  'America/Denver': 'NA',
  'America/Los_Angeles': 'NA',
  'America/Anchorage': 'NA',
  'America/Phoenix': 'NA',
  'America/Toronto': 'NA',
  'America/Vancouver': 'NA',
  'America/Edmonton': 'NA',
  'America/Winnipeg': 'NA',
  'America/Halifax': 'NA',
  'America/Mexico_City': 'NA',
  'America/Monterrey': 'NA',
  'America/Cancun': 'NA',
  'America/Tijuana': 'NA',
  'America/Mazatlan': 'NA',
  'America/Guatemala': 'NA',
  'America/Belize': 'NA',
  'America/Costa_Rica': 'NA',
  'America/El_Salvador': 'NA',
  'America/Tegucigalpa': 'NA',
  'America/Managua': 'NA',
  'America/Panama': 'NA',
  'America/Havana': 'NA',
  'America/Jamaica': 'NA',
  'America/Port-au-Prince': 'NA',
  'America/Santo_Domingo': 'NA',

  // South America
  'America/Sao_Paulo': 'SA',
  'America/Buenos_Aires': 'SA',
  'America/Bogota': 'SA',
  'America/Lima': 'SA',
  'America/Santiago': 'SA',
  'America/Caracas': 'SA',
  'America/La_Paz': 'SA',
  'America/Guayaquil': 'SA',
  'America/Montevideo': 'SA',
  'America/Asuncion': 'SA',
  'America/Cayenne': 'SA',
  'America/Paramaribo': 'SA',
  'America/Fortaleza': 'SA',
  'America/Recife': 'SA',
  'America/Manaus': 'SA',

  // Europe
  'Europe/London': 'EU',
  'Europe/Paris': 'EU',
  'Europe/Berlin': 'EU',
  'Europe/Rome': 'EU',
  'Europe/Madrid': 'EU',
  'Europe/Amsterdam': 'EU',
  'Europe/Brussels': 'EU',
  'Europe/Vienna': 'EU',
  'Europe/Stockholm': 'EU',
  'Europe/Copenhagen': 'EU',
  'Europe/Oslo': 'EU',
  'Europe/Helsinki': 'EU',
  'Europe/Warsaw': 'EU',
  'Europe/Prague': 'EU',
  'Europe/Budapest': 'EU',
  'Europe/Bucharest': 'EU',
  'Europe/Athens': 'EU',
  'Europe/Istanbul': 'EU',
  'Europe/Moscow': 'EU',
  'Europe/Kiev': 'EU',
  'Europe/Lisbon': 'EU',
  'Europe/Dublin': 'EU',
  'Europe/Zurich': 'EU',
  'Europe/Belgrade': 'EU',
  'Europe/Sofia': 'EU',
  'Europe/Riga': 'EU',
  'Europe/Tallinn': 'EU',
  'Europe/Vilnius': 'EU',

  // Africa
  'Africa/Cairo': 'AF',
  'Africa/Lagos': 'AF',
  'Africa/Johannesburg': 'AF',
  'Africa/Nairobi': 'AF',
  'Africa/Algiers': 'AF',
  'Africa/Casablanca': 'AF',
  'Africa/Tunis': 'AF',
  'Africa/Accra': 'AF',
  'Africa/Dar_es_Salaam': 'AF',
  'Africa/Addis_Ababa': 'AF',
  'Africa/Khartoum': 'AF',
  'Africa/Kampala': 'AF',
  'Africa/Kinshasa': 'AF',
  'Africa/Luanda': 'AF',
  'Africa/Maputo': 'AF',
  'Africa/Lusaka': 'AF',
  'Africa/Harare': 'AF',
  'Africa/Windhoek': 'AF',
  'Africa/Abidjan': 'AF',
  'Africa/Dakar': 'AF',

  // Asia
  'Asia/Shanghai': 'AS',
  'Asia/Tokyo': 'AS',
  'Asia/Seoul': 'AS',
  'Asia/Hong_Kong': 'AS',
  'Asia/Singapore': 'AS',
  'Asia/Bangkok': 'AS',
  'Asia/Manila': 'AS',
  'Asia/Jakarta': 'AS',
  'Asia/Kuala_Lumpur': 'AS',
  'Asia/Ho_Chi_Minh': 'AS',
  'Asia/Taipei': 'AS',
  'Asia/Dubai': 'AS',
  'Asia/Kolkata': 'AS',
  'Asia/Mumbai': 'AS',
  'Asia/Delhi': 'AS',
  'Asia/Karachi': 'AS',
  'Asia/Dhaka': 'AS',
  'Asia/Tehran': 'AS',
  'Asia/Baghdad': 'AS',
  'Asia/Riyadh': 'AS',
  'Asia/Kuwait': 'AS',
  'Asia/Jerusalem': 'AS',
  'Asia/Beirut': 'AS',
  'Asia/Damascus': 'AS',
  'Asia/Amman': 'AS',
  'Asia/Kabul': 'AS',
  'Asia/Tashkent': 'AS',
  'Asia/Almaty': 'AS',
  'Asia/Yangon': 'AS',
  'Asia/Colombo': 'AS',
  'Asia/Kathmandu': 'AS',

  // Oceania
  'Australia/Sydney': 'OC',
  'Australia/Melbourne': 'OC',
  'Australia/Brisbane': 'OC',
  'Australia/Perth': 'OC',
  'Australia/Adelaide': 'OC',
  'Australia/Darwin': 'OC',
  'Australia/Hobart': 'OC',
  'Pacific/Auckland': 'OC',
  'Pacific/Fiji': 'OC',
  'Pacific/Port_Moresby': 'OC',
  'Pacific/Guam': 'OC',
  'Pacific/Honolulu': 'OC',
  'Pacific/Tahiti': 'OC',
  'Pacific/Noumea': 'OC',
  'Pacific/Tongatapu': 'OC',
  'Pacific/Apia': 'OC',
};

/**
 * Country code to region mapping (ISO 3166-1 alpha-2)
 * Used as fallback when timezone detection isn't available
 */
const COUNTRY_TO_REGION: Record<string, Region> = {
  // North America
  'US': 'NA', 'CA': 'NA', 'MX': 'NA', 'GT': 'NA', 'BZ': 'NA',
  'CR': 'NA', 'SV': 'NA', 'HN': 'NA', 'NI': 'NA', 'PA': 'NA',
  'CU': 'NA', 'JM': 'NA', 'HT': 'NA', 'DO': 'NA',

  // South America
  'BR': 'SA', 'AR': 'SA', 'CO': 'SA', 'PE': 'SA', 'CL': 'SA',
  'VE': 'SA', 'BO': 'SA', 'EC': 'SA', 'UY': 'SA', 'PY': 'SA',
  'GF': 'SA', 'SR': 'SA', 'GY': 'SA',

  // Europe
  'GB': 'EU', 'FR': 'EU', 'DE': 'EU', 'IT': 'EU', 'ES': 'EU',
  'NL': 'EU', 'BE': 'EU', 'AT': 'EU', 'SE': 'EU', 'DK': 'EU',
  'NO': 'EU', 'FI': 'EU', 'PL': 'EU', 'CZ': 'EU', 'HU': 'EU',
  'RO': 'EU', 'GR': 'EU', 'TR': 'EU', 'RU': 'EU', 'UA': 'EU',
  'PT': 'EU', 'IE': 'EU', 'CH': 'EU', 'RS': 'EU', 'BG': 'EU',
  'LV': 'EU', 'EE': 'EU', 'LT': 'EU', 'SK': 'EU', 'SI': 'EU',
  'HR': 'EU', 'BA': 'EU', 'MK': 'EU', 'AL': 'EU', 'ME': 'EU',

  // Africa
  'EG': 'AF', 'NG': 'AF', 'ZA': 'AF', 'KE': 'AF', 'DZ': 'AF',
  'MA': 'AF', 'TN': 'AF', 'GH': 'AF', 'TZ': 'AF', 'ET': 'AF',
  'SD': 'AF', 'UG': 'AF', 'CD': 'AF', 'AO': 'AF', 'MZ': 'AF',
  'ZM': 'AF', 'ZW': 'AF', 'NA': 'AF', 'CI': 'AF', 'SN': 'AF',

  // Asia
  'CN': 'AS', 'JP': 'AS', 'KR': 'AS', 'HK': 'AS', 'SG': 'AS',
  'TH': 'AS', 'PH': 'AS', 'ID': 'AS', 'MY': 'AS', 'VN': 'AS',
  'TW': 'AS', 'AE': 'AS', 'IN': 'AS', 'PK': 'AS', 'BD': 'AS',
  'IR': 'AS', 'IQ': 'AS', 'SA': 'AS', 'KW': 'AS', 'IL': 'AS',
  'LB': 'AS', 'SY': 'AS', 'JO': 'AS', 'AF': 'AS', 'UZ': 'AS',
  'KZ': 'AS', 'MM': 'AS', 'LK': 'AS', 'NP': 'AS',

  // Oceania
  'AU': 'OC', 'NZ': 'OC', 'FJ': 'OC', 'PG': 'OC', 'GU': 'OC',
  'PF': 'OC', 'NC': 'OC', 'TO': 'OC', 'WS': 'OC',
};

/**
 * Detect region from timezone string
 */
function detectRegionFromTimezone(timezone: string): Region | null {
  // Direct match
  if (TIMEZONE_TO_REGION[timezone]) {
    return TIMEZONE_TO_REGION[timezone];
  }

  // Try to match by prefix (e.g., "America/" → NA or SA)
  if (timezone.startsWith('America/')) {
    // Check if it's in North America list
    const isCentralAmerica = timezone.includes('Guatemala') ||
                            timezone.includes('Belize') ||
                            timezone.includes('Costa') ||
                            timezone.includes('Salvador') ||
                            timezone.includes('Honduras') ||
                            timezone.includes('Nicaragua') ||
                            timezone.includes('Panama');
    const isCaribbean = timezone.includes('Havana') ||
                       timezone.includes('Jamaica') ||
                       timezone.includes('Port') ||
                       timezone.includes('Santo');

    if (isCentralAmerica || isCaribbean) return 'NA';

    // Check if it's South America
    const keywords = ['Sao', 'Buenos', 'Bogota', 'Lima', 'Santiago', 'Caracas',
                     'La_Paz', 'Guayaquil', 'Montevideo', 'Asuncion', 'Cayenne',
                     'Paramaribo', 'Fortaleza', 'Recife', 'Manaus'];
    if (keywords.some(k => timezone.includes(k))) return 'SA';

    // Default America/* to North America (most common)
    return 'NA';
  }

  if (timezone.startsWith('Europe/')) return 'EU';
  if (timezone.startsWith('Africa/')) return 'AF';
  if (timezone.startsWith('Asia/')) return 'AS';
  if (timezone.startsWith('Australia/') || timezone.startsWith('Pacific/')) return 'OC';

  return null;
}

/**
 * Detect region from language/locale code
 */
function detectRegionFromLanguage(language: string): Region | null {
  // Extract country code from language tag (e.g., "en-US" → "US")
  const countryCode = language.split('-')[1]?.toUpperCase();

  if (countryCode && COUNTRY_TO_REGION[countryCode]) {
    return COUNTRY_TO_REGION[countryCode];
  }

  return null;
}

/**
 * Main function to detect user region
 * Returns region info with detection method used
 */
export function detectUserRegion(): RegionInfo {
  try {
    // Method 1: Try timezone detection (most reliable)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timezone) {
      const region = detectRegionFromTimezone(timezone);
      if (region) {
        return {
          region,
          timezone,
          detectionMethod: 'timezone'
        };
      }
    }

    // Method 2: Fallback to language detection
    const language = navigator.language || (navigator as any).userLanguage;

    if (language) {
      const region = detectRegionFromLanguage(language);
      if (region) {
        return {
          region,
          language,
          detectionMethod: 'language'
        };
      }
    }

    // Method 3: Unable to detect
    return {
      region: 'UNKNOWN',
      detectionMethod: 'unknown'
    };

  } catch (error) {
    return {
      region: 'UNKNOWN',
      detectionMethod: 'unknown'
    };
  }
}

/**
 * Get region display name
 */
export function getRegionName(region: Region): string {
  const names: Record<Region, string> = {
    'NA': 'North America',
    'SA': 'South America',
    'EU': 'Europe',
    'AF': 'Africa',
    'AS': 'Asia',
    'OC': 'Oceania',
    'UNKNOWN': 'Unknown'
  };
  return names[region];
}
